
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ConsoleUser, UserRole } from "@/types/rbac";

export const useConsoleUsers = () => {
  return useQuery({
    queryKey: ["console_users"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { action: "list_users" },
        headers: { Authorization: `Bearer ${session.session.access_token}` },
      });
      if (error) throw error;

      // The edge function returns an array of ConsoleUser or an object with users
      const users = Array.isArray(data) ? data : data?.users;
      return (users || []) as ConsoleUser[];
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      email, 
      password, 
      firstName, 
      lastName 
    }: { 
      email: string, 
      password: string, 
      firstName?: string, 
      lastName?: string 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { 
          email, 
          password, 
          firstName, 
          lastName,
          action: "create_user" 
        },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("User created successfully");
      // Add delay before invalidating to ensure API propagation
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["console_users"] });
        queryClient.refetchQueries({ queryKey: ["console_users"] });
      }, 1000);
    },
    onError: (error) => {
      toast.error(`Failed to create user: ${error.message}`);
    }
  });
};

export const useAssignRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string, roleId: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { user_id: userId, role_id: roleId, action: "assign_role" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Role assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error) => {
      toast.error(`Failed to assign role: ${error.message}`);
    }
  });
};

export const useRemoveRole = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-roles", {
        body: { id, action: "remove_role" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Role removed successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error) => {
      toast.error(`Failed to remove role: ${error.message}`);
    }
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session) {
        throw new Error("Not authenticated");
      }
      
      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { userId, action: "delete_user" },
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("User deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
    },
    onError: (error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    }
  });
};

export const useImportDirectoryUser = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      directoryUserId,
      roleIds 
    }: { 
      directoryUserId: string,
      roleIds: string[]
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) throw new Error("Not authenticated");
      
      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { 
          action: "import_directory_user",
          directoryUserId,
          roleIds
        },
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("User imported successfully");
      queryClient.invalidateQueries({ queryKey: ["console_users"] });
      queryClient.invalidateQueries({ queryKey: ["unified_users"] });
    },
    onError: (error) => {
      toast.error(`Failed to import user: ${error.message}`);
    }
  });
};
